import { ref, computed, onMounted, watch } from "vue";
import signatureMakercartService from "@/services/signatureMakercartService";
import { useRoute, useRouter } from "vue-router";

export function useSignatureMakercart(props = {}, emit = null) {
  const router = useRouter();
  const route = useRoute();

  // State - Initialize from props if provided
  const selectedSort = ref(props.selectedSort || "newest");
  const selectedSeries = ref("all");
  const selectedLocale = ref(props.activeLocale || "US");
  const signatureItems = ref([]);
  const isLoading = ref(false);
  const error = ref(null);
  const errorCode = ref(null);
  const totalPages = ref(0);
  const currentPage = ref(1);
  const showModal = ref(false);
  const selectedMakercartId = ref(null);

  // Computed properties
  const emptyStateTitle = computed(() => {
    return "No signature makercart activities available";
  });

  const notFoundMessage = computed(() => {
    return "No signature makercart activities are currently available. Please check back later.";
  });

  const visiblePages = computed(() => {
    const pages = [];
    const maxVisible = 5;
    const start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages.value, start + maxVisible - 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  });

  // Methods
  const fetchSignatureMakercarts = async () => {
    try {
      isLoading.value = true;
      error.value = null;
      errorCode.value = null;

      const params = {
        page: currentPage.value,
        limit: 10,
        search: props.searchQuery || "",
      };

      // Add sorting parameters based on selectedSort
      switch (selectedSort.value) {
        case "top-activities":
          params.sort_field = "Popular";
          params.sort_dir = "desc";
          break;
        case "newest":
          params.sort_field = "ID";
          params.sort_dir = "desc";
          break;
        case "oldest":
          params.sort_field = "ID";
          params.sort_dir = "asc";
          break;
        case "a-z":
          params.sort_field = "Cart Title";
          params.sort_dir = "asc";
          break;
        case "z-a":
          params.sort_field = "Cart Title";
          params.sort_dir = "desc";
          break;
        default:
          params.sort_field = "ID";
          params.sort_dir = "desc";
          break;
      }
      if (selectedSeries.value !== "all") {
        params.series = selectedSeries.value;
      }

      const response =
        await signatureMakercartService.getSignatureMakercart(params);

      if (response.success) {
        // Map API response to component expected format
        signatureItems.value = (response.data.rows || []).map((item) => {
          // Prefer upper_elementary_activities; fall back to middle_school_activities
          const activities = item.upper_elementary_activities?.length
            ? item.upper_elementary_activities
            : item.middle_school_activities || [];

          /**
           * Scan forward from `startIndex` and return the first image_thumbnail
           * found in any activity. Returns null when none exist.
           * `excludeIndex` lets us skip an activity already chosen for
           * the other image slot so both slots show different activities.
           */
          const findImage = (startIndex, excludeIndex = -1) => {
            for (let i = startIndex; i < activities.length; i++) {
              if (i === excludeIndex) continue;
              const img = activities[i]?.image_thumbnail?.[0];
              if (img) return { img, index: i };
            }
            return null;
          };

          // Primary image: first activity (from index 0) that has an image
          const primaryResult = findImage(0);
          const primaryImage =
            primaryResult?.img || "/images/content-placeholder.svg";

          // Secondary image: next activity *after* the one used for primary
          // that also has an image (skip the primary activity's index)
          const secondaryResult = findImage(
            primaryResult ? primaryResult.index + 1 : 0,
            primaryResult?.index ?? -1
          );
          const secondaryImage =
            secondaryResult?.img || "/images/content-placeholder.svg";

          // Use the first available activity for metadata fields
          const firstActivity = activities?.[0];

          return {
            id: item.id,
            makercart_id: item.id,
            makercart_activity: item.title,
            title: item.title,
            subtitle: item.subheader,
            theme: item.theme,
            description: item.description,
            image: primaryImage,
            primaryImage,
            secondaryImage,
            liked: false, // Default value, will be updated by like functionality
            grade_level: firstActivity?.grade_level || [],
            subject: firstActivity?.subject || "General",
            duration: firstActivity?.duration || "N/A",
            tags: firstActivity?.tags || [],
            series: item.series,
          };
        });
        totalPages.value = response.data.totalPages || 0;
        currentPage.value = response.data.page || 1;
      } else {
        error.value = response.message || "Failed to load signature makercarts";
        errorCode.value = response.error;
      }
    } catch (err) {
      error.value =
        err.message || "An error occurred while loading signature makercarts";
      errorCode.value = "FETCH_ERROR";
    } finally {
      isLoading.value = false;
    }
  };

  const updateLikedStatus = (data) => {
    // Update the liked status in the data
    const item = signatureItems.value.find((item) => item.id === data.id);
    if (item) {
      item.liked = data.liked;
    }
  };

  const handleItemClicked = (makercartId) => {
    selectedMakercartId.value = makercartId;

    if (makercartId) {
      const query = { ...route.query };
      query.item = makercartId;
      router.replace({ query });
    }
    showModal.value = true;
  };

  const closeModal = () => {
    showModal.value = false;
    selectedMakercartId.value = null;

    const query = { ...route.query };
    delete query.item;
    router.replace({ query });
  };

  const changePage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page;
      fetchSignatureMakercarts();
    }
  };

  const onSortSelectChange = (payload) => {
    if (!payload) return;

    selectedSort.value = payload;
    currentPage.value = 1; // Reset to first page when sort changes

    // Emit to parent if emit is available
    if (emit) {
      emit("update:selectedSort", payload);
      emit("sort-changed", payload);
    }

    fetchSignatureMakercarts();
  };

  const onSeriesSelectChange = (payload) => {
    if (!payload) return;

    selectedSeries.value = payload;
    currentPage.value = 1; // Reset to first page when series changes
    fetchSignatureMakercarts();
  };

  const onLocaleSelectChange = (payload) => {
    if (!payload) return;

    selectedLocale.value = payload;
    currentPage.value = 1; // Reset to first page when locale changes

    // Emit to parent if emit is available
    if (emit) {
      emit("update:selectedLocale", payload);
      emit("locale-changed", payload);
    }

    fetchSignatureMakercarts();
  };

  // Lifecycle hooks
  onMounted(() => {
    fetchSignatureMakercarts();

    // Check if there's an item query parameter to open the modal
    if (route.query.item) {
      const itemId = route.query.item;
      handleItemClicked(itemId);
    }
  });

  // Watch for prop changes to refetch data
  watch(
    () => [props.searchQuery, props.selectedChips],
    () => {
      currentPage.value = 1; // Reset to first page when filters change
      fetchSignatureMakercarts();
    },
    { deep: true }
  );

  // Watch for external sort and locale changes from parent
  watch(
    () => props.selectedSort,
    (newSort) => {
      if (newSort && newSort !== selectedSort.value) {
        selectedSort.value = newSort;
        currentPage.value = 1;
        fetchSignatureMakercarts();
      }
    }
  );

  watch(
    () => props.activeLocale,
    (newLocale) => {
      if (newLocale && newLocale !== selectedLocale.value) {
        selectedLocale.value = newLocale;
        currentPage.value = 1;
        fetchSignatureMakercarts();
      }
    }
  );

  // Track if signature modal is open (for use by parent components)
  const isSignatureModalOpen = computed(() => showModal.value);

  return {
    // State
    selectedSort,
    selectedSeries,
    selectedLocale,
    signatureItems,
    isLoading,
    error,
    errorCode,
    totalPages,
    currentPage,
    showModal,
    selectedMakercartId,

    // Computed
    emptyStateTitle,
    notFoundMessage,
    visiblePages,
    isSignatureModalOpen,

    // Methods
    fetchSignatureMakercarts,
    updateLikedStatus,
    handleItemClicked,
    closeModal,
    changePage,
    onSortSelectChange,
    onSeriesSelectChange,
    onLocaleSelectChange,
  };
}
